import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { AISettings, UpdateSettingFunction } from './types';
import { Clock, Sun, Moon } from 'lucide-react';

/**
 * O que faz: Seção de Modo de Operação da IA
 * Onde usa: AISettingsForm
 * 
 * Permite definir se a IA funcionará 24/7 ou apenas
 * nos horários comerciais configurados
 * 
 * REFATORADO: Cards visuais para cada opção de modo
 */

interface AIOperatingModeSectionProps {
  settings: AISettings;
  updateSetting: UpdateSettingFunction;
  readOnly?: boolean;
}

export const AIOperatingModeSection = ({
  settings,
  updateSetting,
  readOnly = false
}: AIOperatingModeSectionProps) => {
  return (
    <Card className="rounded-2xl border-border shadow-sm hover:shadow-md transition-shadow duration-200">
      <CardHeader className="pb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-accent flex items-center justify-center">
            <Clock className="w-5 h-5 text-accent-foreground" />
          </div>
          <div>
            <CardTitle className="text-base font-semibold text-foreground">
              Modo de Operação
            </CardTitle>
            <CardDescription className="text-sm text-muted-foreground mt-0.5">
              Quando a IA deve estar disponível
            </CardDescription>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        <RadioGroup
          value={settings.ai_operating_mode}
          onValueChange={value => !readOnly && updateSetting('ai_operating_mode', value)}
          className="grid grid-cols-1 gap-3"
          disabled={readOnly}
        >
          {/* Opção: 24/7 */}
          <Label
            htmlFor="mode-24-7"
            className={`flex items-center gap-4 p-4 rounded-xl cursor-pointer transition-all duration-200 ${settings.ai_operating_mode === '24/7'
                ? 'bg-accent border-2 border-ring shadow-sm'
                : 'bg-muted border-2 border-transparent hover:border-border'
              }`}
          >
            <RadioGroupItem value="24/7" id="mode-24-7" disabled={readOnly} className="sr-only" />
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${settings.ai_operating_mode === '24/7'
                ? 'bg-card'
                : 'bg-secondary'
              }`}>
              <Moon className={`w-5 h-5 ${settings.ai_operating_mode === '24/7' ? 'text-accent-foreground' : 'text-muted-foreground'
                }`} />
            </div>
            <div className="flex-1">
              <span className={`font-semibold block ${settings.ai_operating_mode === '24/7'
                  ? 'text-accent-foreground'
                  : 'text-foreground'
                }`}>
                24/7 - Sempre ativo
              </span>
              <span className={`text-sm ${settings.ai_operating_mode === '24/7'
                  ? 'text-accent-foreground'
                  : 'text-muted-foreground'
                }`}>
                A IA funciona a qualquer hora, todos os dias
              </span>
            </div>
            {settings.ai_operating_mode === '24/7' && (
              <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                <svg className="w-4 h-4 text-primary-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            )}
          </Label>

          {/* Opção: Horário Comercial */}
          <Label
            htmlFor="mode-business"
            className={`flex items-center gap-4 p-4 rounded-xl cursor-pointer transition-all duration-200 ${settings.ai_operating_mode === 'horario_comercial'
                ? 'bg-accent border-2 border-ring shadow-sm'
                : 'bg-muted border-2 border-transparent hover:border-border'
              }`}
          >
            <RadioGroupItem value="horario_comercial" id="mode-business" disabled={readOnly} className="sr-only" />
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${settings.ai_operating_mode === 'horario_comercial'
                ? 'bg-card'
                : 'bg-secondary'
              }`}>
              <Sun className={`w-5 h-5 ${settings.ai_operating_mode === 'horario_comercial' ? 'text-accent-foreground' : 'text-muted-foreground'
                }`} />
            </div>
            <div className="flex-1">
              <span className={`font-semibold block ${settings.ai_operating_mode === 'horario_comercial'
                  ? 'text-accent-foreground'
                  : 'text-foreground'
                }`}>
                Horário comercial
              </span>
              <span className={`text-sm ${settings.ai_operating_mode === 'horario_comercial'
                  ? 'text-accent-foreground'
                  : 'text-muted-foreground'
                }`}>
                Funciona apenas nos horários configurados
              </span>
            </div>
            {settings.ai_operating_mode === 'horario_comercial' && (
              <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                <svg className="w-4 h-4 text-primary-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            )}
          </Label>
        </RadioGroup>
      </CardContent>
    </Card>
  );
};